import { useContext, useState, useEffect, useRef } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, useConfirmationModal } from 't-lib-ui/structure';
import { getAdminMagicSecret } from '../../connector/auth';
import { TextButton, Note, useToast } from 't-lib-ui/widgets';
import { chevronBackIcon, chevronForwardIcon, addIcon, trashIcon, editIcon, resizeIcon, cloudUploadIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useGetStuff, useSetStuff, useScaleImage, useUploadImage } from '../../connector/api';
import { ImageCollection } from '../../schema-copy/api-schema';
import { QueryStatus } from 't-lib-ui/connector';
import './AdminImagesPage.css';

interface ImageItem {
  name: string;
  url: string;
}

interface ImageCardProps {
  image: ImageItem;
  onDelete: (name: string) => void;
  onEdit: (name: string) => void;
}

const ImageCard: React.FC<ImageCardProps> = ({ image, onDelete, onEdit }) => {
  const [showFullImage, setShowFullImage] = useState(false);

  const showConfirmDelete = useConfirmationModal({
    title: 'Delete Image?',
    confirmContent: 'Delete',
    dismissContent: 'Cancel',
    children: `Are you sure you want to remove "${image.name}" from the collection? Note: The image will remain on the CDN.`,
    onConfirm: () => onDelete(image.name)
  });

  const w400 = image.url.replace(/\.(png|jpg|jpeg|webp|gif)$/i, '-w400.webp');
  const w600 = image.url.replace(/\.(png|jpg|jpeg|webp|gif)$/i, '-w600.webp');

return (
    <>
      <div className="adminImageCard">
        <div
          className="adminImageThumbnail"
          onClick={() => setShowFullImage(true)}
          style={{ cursor: 'pointer' }}
        >
          <img
            src={w400}
            srcSet={`${w400} 400w, ${w600} 600w`}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 600px, 400px"
            alt={image.name}
            onError={(e) => {
              // Fallback to original image if scaled versions don't exist
              (e.target as HTMLImageElement).src = image.url;
            }}
          />
        </div>
        <div className="adminImageInfo">
          <div className="adminImageName">{image.name}</div>
          <div className="adminImageActions">
            <TextButton
              label="Edit"
              icon={editIcon}
              onClick={() => onEdit(image.name)}
              focussable="flow"
              size="small"
              type="minor"
            />
            <TextButton
              label="Delete"
              icon={trashIcon}
              onClick={showConfirmDelete}
              focussable="flow"
              size="small"
              type="minor"
            />
          </div>
        </div>
      </div>

      {showFullImage && (
        <div
          className="adminImageFullOverlay"
          onClick={() => setShowFullImage(false)}
        >
          <div className="adminImageFullContainer">
            <img
              src={image.url}
              alt={image.name}
              className="adminImageFull"
            />
            <div className="adminImageFullName">{image.name}</div>
          </div>
        </div>
      )}
    </>
  );
};

const AdminImagesPage: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [originalImages, setOriginalImages] = useState<ImageItem[]>([]);
  const [showToast, toastProps] = useToast();
  const [rescalingProgress, setRescalingProgress] = useState<{ current: string; remaining: number } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [page, setPage] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const PAGE_SIZE = 60;

  const magic = getAdminMagicSecret() || adminSecret || '';
  const scaleImage = useScaleImage(magic);
  const uploadImage = useUploadImage(magic);

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminImagesPage', {
      'Escape': () => {
        goTo(routerCtx, '/a/admin/feeds');
        return true;
      },
    }, false, 0);

    return () => unregisterKeyHandler('adminImagesPage');
  }, [routerCtx]);

  // Load image collection
  const { data: imageCollection, status: loadStatus } = useGetStuff<ImageCollection>('image-collection', magic);
  const setStuff = useSetStuff<ImageCollection>('image-collection', magic);

  // Initialize images from loaded collection
  useEffect(() => {
    if (imageCollection?.images) {
      setImages(imageCollection.images);
      setOriginalImages(JSON.parse(JSON.stringify(imageCollection.images)));
    }
  }, [imageCollection]);

  const handleBack = () => {
    goTo(routerCtx, '/a/admin/feeds');
  };

  const handleAddImage = () => {
    goTo(routerCtx, '/a/admin/image/new');
  };

  const handleDeleteImage = async (name: string) => {
    const newImages = images.filter(img => img.name != name);
    setImages(newImages);

    try {
      const newCollection: ImageCollection = { images: newImages };
      await setStuff(newCollection);
      setOriginalImages(JSON.parse(JSON.stringify(newImages)));
      showToast({ children: `Image "${name}" removed from collection`, type: 'success' }, 3000);
    } catch (error) {
      console.error('Error removing image:', error);
      showToast({ children: 'Error removing image from collection', type: 'error' }, 3000);
      // Revert on error
      setImages(originalImages);
    }
  };

  const handleEditImage = (name: string) => {
    goTo(routerCtx, `/a/admin/image/${name}`);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      showToast({ children: 'Please select a valid image file (JPEG, PNG, WebP, or GIF)', type: 'error' }, 4000);
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast({ children: 'Image too large (max 10MB)', type: 'error' }, 4000);
      return;
    }

    // Extract name from filename (without extension)
    const defaultName = file.name.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '').toLowerCase().replace(/[^a-z0-9_-]/g, '-');

    // Prompt for name
    const name = prompt('Enter image name (alphanumeric, hyphens, underscores only):', defaultName);
    if (!name) {
      // User cancelled
      event.target.value = '';
      return;
    }

    // Validate name
    if (!/^[a-z0-9_-]+$/i.test(name)) {
      showToast({ children: 'Image name must be alphanumeric with hyphens/underscores only', type: 'error' }, 4000);
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadImage({ image: file, name: name.toLowerCase() });
      showToast({ children: `Image "${result.name}" uploaded successfully`, type: 'success' }, 3000);

      // Update local state with the new image
      const newImage: ImageItem = { name: result.name, url: result.url };
      const existingIndex = images.findIndex(img => img.name.toLowerCase() == result.name.toLowerCase());

      let newImages: ImageItem[];
      if (existingIndex >= 0) {
        // Replace existing image
        newImages = [...images];
        newImages[existingIndex] = newImage;
      } else {
        // Add new image
        newImages = [...images, newImage];
      }

      setImages(newImages);
      setOriginalImages(JSON.parse(JSON.stringify(newImages)));
    } catch (error: any) {
      console.error('Error uploading image:', error);
      showToast({ children: `Error uploading image: ${error.message || 'Unknown error'}`, type: 'error' }, 4000);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRescaleAll = async () => {
    if (!images.length) return;

    const total = images.length;
    try {
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        setRescalingProgress({
          current: image.name,
          remaining: total - i - 1
        });

        try {
          await scaleImage(image.name);
        } catch (error) {
          console.error(`Error scaling image ${image.name}:`, error);
          showToast({ children: `Error scaling ${image.name}: ${error}`, type: 'error' }, 5000);
        }
      }

      setRescalingProgress(null);
      showToast({ children: `Successfully rescaled ${total} images`, type: 'success' }, 4000);
    } catch (error) {
      setRescalingProgress(null);
      console.error('Error during rescaling:', error);
      showToast({ children: 'Error during rescaling process', type: 'error' }, 4000);
    }
  };

  const showConfirmRescale = useConfirmationModal({
    title: 'Rescale All Images?',
    confirmContent: 'Rescale All',
    dismissContent: 'Cancel',
    children: `This will regenerate scaled versions (286px, 600px, 1250px) for all ${images.length} images. This process may take several minutes.`,
    onConfirm: handleRescaleAll
  });

  const loading = loadStatus == QueryStatus.Loading;
  const isRescaling = rescalingProgress != null;

  // Latest images first
  const orderedImages = [...images].reverse();
  const pageCount = Math.ceil(orderedImages.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(0, pageCount - 1));
  const pagedImages = orderedImages.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-images"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <div className="adminImagesContainer">
                <AdminHeader title="Manage Images" onExpand={onExpand} />
                <p className="adminImagesSubtitle">
                  {loading ? 'Loading...' : isUploading ? 'Uploading...' : `${images.length} images in collection`}
                </p>

                <ButtonBar size="small">
                  <TextButton
                    label="Back"
                    icon={chevronBackIcon}
                    onClick={handleBack}
                    focussable="flow"
                    enabled={isRescaling || isUploading ? 'disabled' : 'enabled'}
                  />
                  <TextButton
                    label="Add Image"
                    icon={addIcon}
                    onClick={handleAddImage}
                    focussable="flow"
                    type='major'
                    enabled={isRescaling || isUploading ? 'disabled' : 'enabled'}
                  />
                  <TextButton
                    label="Upload Image"
                    icon={cloudUploadIcon}
                    onClick={handleUploadClick}
                    focussable="flow"
                    type='minor'
                    enabled={isRescaling || isUploading ? 'disabled' : 'enabled'}
                  />
                  <TextButton
                    label="Rescale All"
                    icon={resizeIcon}
                    onClick={showConfirmRescale}
                    focussable="flow"
                    type='minor'
                    enabled={isRescaling || isUploading || !images.length ? 'disabled' : 'enabled'}
                  />
                </ButtonBar>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileSelected}
                  style={{ display: 'none' }}
                />

                <Note {...toastProps} />

                {isUploading && (
                  <div className="adminImagesLoading">
                    Uploading image...
                  </div>
                )}

                {isRescaling && rescalingProgress && (
                  <div className="adminImagesLoading">
                    Rescaling images... Currently processing: <strong>{rescalingProgress.current}</strong>
                    <br />
                    {rescalingProgress.remaining} image{rescalingProgress.remaining == 1 ? '' : 's'} remaining
                  </div>
                )}

                {loading && (
                  <div className="adminImagesLoading">
                    Loading images...
                  </div>
                )}

                {!loading && images.length == 0 && (
                  <div className="adminImagesEmpty">
                    No images in collection yet. Click "Add Image" to create one!
                  </div>
                )}

                {!loading && images.length > 0 && (
                  <>
                    <div className="adminImagesGrid">
                      {pagedImages.map((image) => (
                        <ImageCard
                          key={image.name}
                          image={image}
                          onDelete={handleDeleteImage}
                          onEdit={handleEditImage}
                        />
                      ))}
                    </div>
                    {pageCount > 1 && (
                      <div className="adminImagesPagination">
                        <TextButton
                          label="Previous"
                          icon={chevronBackIcon}
                          onClick={() => setPage(currentPage - 1)}
                          focussable="flow"
                          size="small"
                          type="minor"
                          enabled={currentPage > 0 ? 'enabled' : 'disabled'}
                        />
                        <span className="adminImagesPageInfo">Page {currentPage + 1} of {pageCount}</span>
                        <TextButton
                          label="Next"
                          icon={chevronForwardIcon}
                          onClick={() => setPage(currentPage + 1)}
                          focussable="flow"
                          size="small"
                          type="minor"
                          enabled={currentPage < pageCount - 1 ? 'enabled' : 'disabled'}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminImagesPage;
